import { useMemo } from 'react';

import { CustomerStatusDto, PermissionDto } from '@/api/generated';
import { SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import { useHasPermission } from '@/contexts/userContext/util/useHasPermission.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { customerStatusUtil } from '@/views/customers/status/customerStatus.util.ts';

export type CustomerStatusFieldProps = {
  defaultOption: SelectSearchOptionModel<CustomerStatusDto> | null;
  isLocked: boolean;
  isVisible: boolean;
  options: SelectSearchOptionModel<CustomerStatusDto>[];
};

/** The status field of a customer create or edit form; see `customerStatusUtil.resolveStatusField`. */
export const useCustomerStatusField = (
  currentStatus?: CustomerStatusDto,
): CustomerStatusFieldProps => {
  const { t } = useTranslationWithPrefix('utils.dictionaries.customerStatusOptions');
  const canApprove = useHasPermission(PermissionDto.CustomerApprove);

  return useMemo(() => {
    const field = customerStatusUtil.resolveStatusField({ canApprove, currentStatus });
    const toOption = (status: CustomerStatusDto): SelectSearchOptionModel<CustomerStatusDto> => ({
      label: t(status),
      value: status,
    });
    return {
      defaultOption: field.defaultStatus ? toOption(field.defaultStatus) : null,
      isLocked: field.isLocked,
      isVisible: field.isVisible,
      options: field.statuses.map(toOption),
    };
  }, [canApprove, currentStatus, t]);
};
